import React, { useEffect, useMemo, useState } from 'react';
import { GamepadIcon, SettingsIcon, InfoIcon, XIcon } from 'lucide-react';
import { BotConfigurationManager } from './BotConfigurationManager.js';
import { MermaidDiagram } from './MermaidDiagram.js';
import { useDungeonSummarySettings, useUpdateDungeonSummarySettings } from '../hooks/useDungeon.js';
import { useBotConfigurations } from '../hooks/useBotConfiguration.js';

const DUNGEON_BOT_PROMPT_TYPES = [
  { id: 'adventure_setup', name: 'Adventure Setup' },
  { id: 'adventure_setup_file', name: 'Adventure Setup (File)' },
  { id: 'adventure_setup_summary', name: 'Adventure Setup Summary' },
  { id: 'scene_narration', name: 'Scene Narration' },
  { id: 'story_summary', name: 'Story Summary' },
  { id: 'cyoa_setup', name: 'CYOA Setup' },
  { id: 'cyoa_narration', name: 'CYOA Narration' },
];

const DEFAULT_PROVIDER = 'openrouter';
const DEFAULT_MODEL = 'anthropic/claude-3-opus';

const GUARD_PREFIX = `Global Safety & Tone Guard\n- Do not control player characters.\n- Avoid explicit sexual content and real-world hate.\n- Keep outputs Discord-friendly.\n`;

const DUNGEON_BOT_DEFAULT_PROMPTS = {
  adventure_setup: {
    systemPrompt: `${GUARD_PREFIX}\nYou are the Game Master. Generate a vivid opening scene that establishes setting, stakes, and tension. Address the first player by name to invite them to act. Do not write for player characters. Keep it concise (200–350 words).`,
    userPrompt: `World Seed: {adventure_prompt}\nParty: {party_overview}\nFirst Player: {next_player}\nConstraints: {style_guide}`,
  },
  adventure_setup_file: {
    systemPrompt: `${GUARD_PREFIX}\nYou are the Game Master. Generate a vivid opening scene that establishes setting, stakes, and tension using the provided world information. Address the first player by name to invite them to act. Do not write for player characters. Keep it concise (200–350 words).`,
    userPrompt: `User Prompt: {user_prompt}\nWorld Info:\n{file_contents}\n\nParty: {party_overview}\nFirst Player: {next_player}\nConstraints: {style_guide}`,
  },
  adventure_setup_summary: {
    systemPrompt: `${GUARD_PREFIX}\nYou are summarizing the opening scene into a short, reusable title and summary for UI display. Output valid JSON only.`,
    userPrompt: `Opening Scene:\n{opening_text}\n\nUser Prompt: {user_prompt}\nWorld Info:\n{file_contents}`,
  },
  scene_narration: {
    systemPrompt: `${GUARD_PREFIX}\nYou are the Game Master continuing the scene. Describe consequences of the player's action in a dynamic, sensory way. Don't control other PCs. Advance tension; end by addressing the next player by name to prompt their action (150–250 words).`,
    userPrompt: `Context: {story_context}\nParty: {party_overview}\nAction: {last_action}\nNext Player: {next_player}`,
  },
  story_summary: {
    systemPrompt: `${GUARD_PREFIX}\nMaintain a compact "story so far" recap. Preserve names, goals, locations, unresolved hooks. Keep it tight and readable.`,
    userPrompt: `Recent History:\n{recent_history}\n\nSummary Window: {turn_count} rounds`,
  },
  cyoa_setup: {
    systemPrompt: `${GUARD_PREFIX}\nYou are the Game Master creating a Choose Your Own Adventure opening. Generate a vivid opening scene (150-250 words) that establishes setting and tension. End by addressing the first player by name and presenting exactly 4 distinct action choices. Output valid JSON only with keys "narrative" (string) and "choices" (array of objects with "id" (1-4) and "text" (short action description, 5-15 words)).`,
    userPrompt: `World Seed: {adventure_prompt}\nParty: {party_overview}\nFirst Player: {next_player}\nConstraints: {style_guide}\n\nOutput JSON: {"narrative": "...", "choices": [{"id": 1, "text": "..."}, {"id": 2, "text": "..."}, {"id": 3, "text": "..."}, {"id": 4, "text": "..."}]}`,
  },
  cyoa_narration: {
    systemPrompt: `${GUARD_PREFIX}\nYou are the Game Master continuing a Choose Your Own Adventure. Describe the consequences of the chosen action (100-200 words) with dynamic sensory detail. Refer to players in third person by their character name. End by addressing the next player by name and presenting exactly 4 new distinct action choices. Output valid JSON only with keys "narrative" (string) and "choices" (array of objects with "id" (1-4) and "text" (short action description, 5-15 words)).`,
    userPrompt: `Context: {story_context}\nParty: {party_overview}\nChosen Action: {last_action}\nNext Player: {next_player}\n\nOutput JSON: {"narrative": "...", "choices": [{"id": 1, "text": "..."}, {"id": 2, "text": "..."}, {"id": 3, "text": "..."}, {"id": 4, "text": "..."}]}`,
  },
};

const DUNGEON_BOT_VARIABLE_HINTS: Record<string, { name: string; description: string }[]> = {
  adventure_setup: [
    { name: 'adventure_prompt', description: 'Original adventure theme supplied when the lobby was created.' },
    { name: 'party_overview', description: 'Formatted list of participating players and character descriptions.' },
    { name: 'next_player', description: 'The character name of the first player in turn order.' },
    { name: 'style_guide', description: 'Optional style constraints configured for this session.' }
  ],
  adventure_setup_file: [
    { name: 'user_prompt', description: 'Optional user prompt supplied with the file.' },
    { name: 'file_contents', description: 'World info extracted from the uploaded file.' },
    { name: 'party_overview', description: 'Formatted list of participating players and character descriptions.' },
    { name: 'next_player', description: 'The character name of the first player in turn order.' },
    { name: 'style_guide', description: 'Optional style constraints configured for this session.' }
  ],
  adventure_setup_summary: [
    { name: 'opening_text', description: 'Generated opening scene text.' },
    { name: 'user_prompt', description: 'Optional user prompt supplied with the file.' },
    { name: 'file_contents', description: 'World info extracted from the uploaded file.' }
  ],
  scene_narration: [
    { name: 'story_context', description: 'Recent narrative context of the ongoing adventure.' },
    { name: 'party_overview', description: 'Formatted list of participating players and character descriptions.' },
    { name: 'last_action', description: 'The latest action text submitted by the active player.' },
    { name: 'next_player', description: 'The character name of the player whose turn is next.' }
  ],
  story_summary: [
    { name: 'recent_history', description: 'Recent story events to summarize (auto-populated).' },
    { name: 'turn_count', description: 'Summary window size in rounds between summaries.' }
  ],
  cyoa_setup: [
    { name: 'adventure_prompt', description: 'Original adventure theme supplied when the lobby was created.' },
    { name: 'party_overview', description: 'Formatted list of participating players and character descriptions.' },
    { name: 'next_player', description: 'The character name of the first player in turn order.' },
    { name: 'style_guide', description: 'Optional style constraints configured for this session.' }
  ],
  cyoa_narration: [
    { name: 'story_context', description: 'Recent narrative context of the ongoing adventure.' },
    { name: 'party_overview', description: 'Formatted list of participating players and character descriptions.' },
    { name: 'last_action', description: 'The choice text selected by the active player (includes their name).' },
    { name: 'next_player', description: 'The character name of the player whose turn is next.' }
  ],
};

const escapeMermaidValue = (value: string) =>
  value.replace(/[^a-zA-Z0-9._/-]+/g, ' ').replace(/\s+/g, ' ').trim();

const formatModelLabel = (config?: { provider?: string; model?: string; modelProvider?: string }) => {
  const provider = escapeMermaidValue(config?.provider || DEFAULT_PROVIDER);
  const model = escapeMermaidValue(config?.model || DEFAULT_MODEL);
  const modelProvider = config?.modelProvider ? escapeMermaidValue(config.modelProvider) : '';
  const providerLabel = modelProvider ? `${provider} via ${modelProvider}` : provider;
  return `Provider ${providerLabel}, Model ${model}`;
};

const buildDungeonFlowChart = (params: {
  openingLabel: string;
  narrationLabel: string;
  summaryLabel: string;
  summaryRounds: number;
}) => `flowchart TB
    Start[Command: propose] --> Lobby[Players join + set characters]
    Lobby --> Thread[Host starts session]
    Thread --> Opening[Opening scene - ${params.openingLabel}]
    Opening --> TurnLoop[Round loop - each player acts once]

    TurnLoop --> Action[Command: do action]
    Action --> Narration[Narration - ${params.narrationLabel}]
    Narration --> TurnLoop

    TurnLoop -.-> Summary[Summary every ${params.summaryRounds} rounds - ${params.summaryLabel}]
    Summary --> TurnLoop

    TurnLoop --> Pass[Command: pass turn]
    Pass --> TurnLoop

    TurnLoop --> Reroll[Command: reroll last]
    Reroll --> Narration

    TurnLoop --> End[Command: end session]

    style Opening fill:#9f7aea,stroke:#553c9a,color:#fff
    style Narration fill:#9f7aea,stroke:#553c9a,color:#fff
    style Summary fill:#9f7aea,stroke:#553c9a,color:#fff`;

export const DungeonBotConfigPage: React.FC = () => {
  const [showFlowDiagram, setShowFlowDiagram] = useState(false);
  const { data: configurations } = useBotConfigurations('dungeon');
  const { data: summarySettings, isLoading: summaryLoading } = useDungeonSummarySettings('global');
  const summaryMutation = useUpdateDungeonSummarySettings();
  const [summaryRoundsInput, setSummaryRoundsInput] = useState('15');
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    if (summarySettings?.summaryRounds) {
      setSummaryRoundsInput(String(summarySettings.summaryRounds));
    }
  }, [summarySettings?.summaryRounds]);

  const parsedSummaryRounds = Number.parseInt(summaryRoundsInput, 10);
  const summaryRoundsValid = Number.isFinite(parsedSummaryRounds) && parsedSummaryRounds >= 5 && parsedSummaryRounds <= 100;
  const summaryRoundsValue = typeof summarySettings?.summaryRounds === 'number' ? summarySettings.summaryRounds : 15;

  const openingConfig = configurations?.find((config) => config.promptType === 'adventure_setup' && config.isActive);
  const narrationConfig = configurations?.find((config) => config.promptType === 'scene_narration' && config.isActive);
  const summaryConfig = configurations?.find((config) => config.promptType === 'story_summary' && config.isActive);

  const flowChart = useMemo(
    () =>
      buildDungeonFlowChart({
        openingLabel: formatModelLabel(openingConfig),
        narrationLabel: formatModelLabel(narrationConfig),
        summaryLabel: formatModelLabel(summaryConfig),
        summaryRounds: summaryRoundsValue,
      }),
    [openingConfig, narrationConfig, summaryConfig, summaryRoundsValue]
  );

  const handleSaveSummary = async () => {
    if (!summaryRoundsValid) {
      setSummaryError('Summary rounds must be between 5 and 100.');
      return;
    }
    setSummaryError(null);
    await summaryMutation.mutateAsync({ guildId: 'global', summaryRounds: parsedSummaryRounds });
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
        <h1 className="text-2xl font-bold flex items-center">
          <GamepadIcon className="mr-2 text-purple-400" size={24} />
          Dungeon Bot Configuration
        </h1>
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
          <button
            type="button"
            onClick={() => setShowFlowDiagram(true)}
            className="inline-flex items-center gap-1 rounded-md border border-gray-700 bg-gray-800/70 px-3 py-1.5 text-xs text-gray-200 hover:border-purple-500/60 hover:text-white"
          >
            <InfoIcon size={14} />
            View Flow
          </button>
          <div className="flex items-center">
            <SettingsIcon size={16} className="mr-1" />
            Configure prompts and models for RPG game mastering
          </div>
        </div>
      </div>

      <div className="mb-6 bg-gray-800/60 border border-gray-700 rounded-lg p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-gray-200">Global Settings</h3>
              <p className="text-xs text-gray-400">Defaults used when a guild has no override.</p>
            </div>
            <div className="text-xs text-gray-500">
              Source: {summarySettings?.source ?? 'default'}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Summary Rounds</label>
              <input
                type="number"
                min={5}
                max={100}
                value={summaryRoundsInput}
                onChange={(e) => {
                  setSummaryRoundsInput(e.target.value);
                  setSummaryError(null);
                }}
                className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100"
                disabled={summaryLoading}
              />
              <div className="text-xs text-gray-500 mt-2">Number of rounds between automatic story summarization.</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSaveSummary}
                disabled={summaryMutation.isPending || summaryLoading}
                className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 text-white text-sm px-4 py-2 rounded-md"
              >
                {summaryMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              {summaryMutation.isSuccess && !summaryMutation.isPending && (
                <span className="text-xs text-green-400">Saved</span>
              )}
            </div>
          </div>
          {summaryError && <div className="text-xs text-red-400">{summaryError}</div>}
          {summaryMutation.isError && (
            <div className="text-xs text-red-400">Failed to save settings.</div>
          )}
        </div>
      </div>

      <div className="mb-4 p-4 bg-purple-900/20 border border-purple-700 rounded-lg">
        <h3 className="text-sm font-medium text-purple-300 mb-2">Dungeon Bot Prompt Types</h3>
        <p className="text-sm text-purple-200">
          Configure prompts and models for the active Dungeon Bot steps: opening scene, narration, and summary.
        </p>
      </div>

      <div>
        <BotConfigurationManager
          botName="dungeon"
          promptTypes={DUNGEON_BOT_PROMPT_TYPES}
          defaultPrompts={DUNGEON_BOT_DEFAULT_PROMPTS}
          defaultProvider={DEFAULT_PROVIDER}
          defaultModel={DEFAULT_MODEL}
          variableHints={DUNGEON_BOT_VARIABLE_HINTS}
        />
      </div>

      {showFlowDiagram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-5xl rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-800 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-100">Dungeon Bot Turn Flow</h2>
                <p className="text-xs text-gray-400">Mermaid diagram of the current orchestration path.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowFlowDiagram(false)}
                className="rounded-md border border-gray-700 bg-gray-800 p-2 text-gray-300 hover:border-purple-500/60 hover:text-white"
              >
                <XIcon size={16} />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="max-h-[70vh] overflow-auto rounded-md border border-gray-800 bg-gray-950">
                <MermaidDiagram chart={flowChart} className="p-4" />
              </div>
            </div>
            <div className="flex justify-end border-t border-gray-800 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowFlowDiagram(false)}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
